/**
 * ==========================================================================
 *  Pflege_Cockpit.jsx — Phase 4: Externer Pflegecockpit-Bestand + Helpdesk
 * ==========================================================================
 *  Drei Sub-Reiter:
 *    1. Uebersicht  — Aggregat-Kacheln (Gesamt, Liane-Box-Anteil,
 *                     Anteil "auch Beratungskunde")
 *    2. Alle Nutzer — durchsuchbare paginierte Liste der ~15.000.
 *    3. Anfragen    — Helpdesk-View fuer Feedback-Widget-Anfragen aus
 *                     app.pflege-dschungel.de mit Konversations-Verlauf,
 *                     Antwort-Mail, interner Notiz und Telefon-Notiz.
 *
 *  Datenquellen:
 *    - 'pflegecockpit_nutzer' (Nutzer-Bestand)
 *    - 'feedback_anfragen' + 'feedback_nachrichten' (Helpdesk)
 * ==========================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, Users, Search, Mail, Calendar, ShieldCheck, MessageSquare,
  Phone, Send, Loader2, CheckCircle2, Clock, AlertCircle, RefreshCw, Trash2,
  CreditCard, TrendingUp, XCircle, Paperclip, X, UploadCloud, FileText,
} from 'lucide-react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C' };

const TABS = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'nutzer', label: 'Alle Nutzer' },
  { id: 'zahlende', label: 'Zahlende Kunden' },
  { id: 'anfragen', label: 'Anfragen' },
];

const SEITENGROESSE = 50;

const BEREICHE = {
  pflegecockpit: 'Pflege-Cockpit (Allgemeine Frage)',
  // 'allgemein' wird nicht mehr im Widget angeboten, bleibt aber im
  // Mapping fuer historische Anfragen.
  allgemein: 'Allgemein',
  pflegegradrechner: 'Pflegegrad-Rechner',
  verhinderungspflege: 'VHP-Rechner',
  nachbarschaftshilfe: 'NBH-Rechner',
  liane_pflegebox: 'Liane Pflegebox',
};

const STATUS_LABEL = {
  offen: 'Offen',
  in_bearbeitung: 'In Bearbeitung',
  erledigt: 'Erledigt',
};

export default function PflegeCockpit() {
  const [tab, setTab] = useState('uebersicht');
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);

  // Tieflink aus der Notification-Mail: ?feedback=<id> springt direkt
  // in den Anfragen-Tab und selektiert die jeweilige Anfrage.
  const [initialAnfrageId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('feedback') || null;
  });
  useEffect(() => {
    if (initialAnfrageId) setTab('anfragen');
  }, [initialAnfrageId]);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const data = await api.get('/api/pflegecockpit/stats');
        if (!abgebrochen) setStats(data);
      } catch (e) {
        if (!abgebrochen) setStatsError(e?.detail || 'Statistik konnte nicht geladen werden.');
      }
    })();
    return () => { abgebrochen = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: CI.brombeer }}
          >
            <Activity className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Pflege-Cockpit</h1>
        </div>

        <div className="flex gap-1 border-b border-gray-200 mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? 'border-brombeer text-brombeer'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={tab === t.id ? { color: CI.brombeer, borderColor: CI.brombeer } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'uebersicht' && (
          <Uebersicht stats={stats} fehler={statsError} />
        )}
        {tab === 'nutzer' && <NutzerListe />}
        {tab === 'zahlende' && <ZahlendeKunden />}
        {tab === 'anfragen' && <AnfragenAnsicht initialId={initialAnfrageId} />}
      </div>
    </div>
  );
}


function Uebersicht({ stats, fehler }) {
  if (fehler) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
        {fehler}
      </div>
    );
  }
  if (!stats) {
    return <div className="text-sm text-gray-500">Lade Statistik…</div>;
  }
  if (stats.gesamt === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-sm text-amber-900">
        <strong>Noch keine Daten importiert.</strong>
        <div className="mt-2">
          Lade die Excel-Datei aus dem Pflegecockpit per scp auf den Server
          und führe danach <code className="bg-white px-1 py-0.5 rounded">python import_pflegecockpit.py …</code> aus.
        </div>
      </div>
    );
  }
  const lianeProzent = stats.gesamt ? Math.round((stats.liane_box_kunden / stats.gesamt) * 1000) / 10 : 0;
  const matchProzent = stats.gesamt ? Math.round((stats.auch_beratungskunden / stats.gesamt) * 1000) / 10 : 0;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Kachel label="Pflegecockpit-Nutzer gesamt" wert={stats.gesamt.toLocaleString('de-DE')} icon={Users} />
      <Kachel
        label="Davon Liane-Pflegebox-Kunden"
        wert={`${stats.liane_box_kunden.toLocaleString('de-DE')}`}
        zusatz={`${lianeProzent}% des Bestands`}
        icon={ShieldCheck}
      />
      <Kachel
        label="Auch Beratungskunde im Cockpit"
        wert={`${stats.auch_beratungskunden.toLocaleString('de-DE')}`}
        zusatz={`${matchProzent}% gematcht`}
        icon={Activity}
      />
    </div>
  );
}


function Kachel({ label, wert, zusatz, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500">
        <Icon className="w-4 h-4" style={{ color: CI.brombeer }} />
        {label}
      </div>
      <div className="text-3xl font-bold text-gray-800 mt-2">{wert}</div>
      {zusatz && <div className="text-xs text-gray-500 mt-1">{zusatz}</div>}
    </div>
  );
}


function NutzerListe() {
  const [suche, setSuche] = useState('');
  const [lianeFilter, setLianeFilter] = useState('all');  // 'all' | 'true' | 'false'
  const [seite, setSeite] = useState(0);
  const [daten, setDaten] = useState({ total: 0, items: [] });
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    setLaden(true);
    setFehler(null);
    const params = new URLSearchParams();
    if (suche.trim()) params.set('q', suche.trim());
    if (lianeFilter === 'true') params.set('liane', 'true');
    if (lianeFilter === 'false') params.set('liane', 'false');
    params.set('limit', String(SEITENGROESSE));
    params.set('offset', String(seite * SEITENGROESSE));
    (async () => {
      try {
        const data = await api.get(`/api/pflegecockpit/nutzer?${params}`);
        if (!abgebrochen) setDaten(data);
      } catch (e) {
        if (!abgebrochen) setFehler(e?.detail || 'Liste konnte nicht geladen werden.');
      } finally {
        if (!abgebrochen) setLaden(false);
      }
    })();
    return () => { abgebrochen = true; };
  }, [suche, lianeFilter, seite]);

  const seitenAnzahl = Math.max(1, Math.ceil(daten.total / SEITENGROESSE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={suche}
            onChange={(e) => { setSuche(e.target.value); setSeite(0); }}
            placeholder="Suche nach Email, Name oder Versicherungsnummer…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          />
        </div>
        <select
          value={lianeFilter}
          onChange={(e) => { setLianeFilter(e.target.value); setSeite(0); }}
          className="border border-gray-300 rounded-lg text-sm bg-white px-3 py-2"
        >
          <option value="all">Alle</option>
          <option value="true">Nur Liane-Pflegebox-Kunden</option>
          <option value="false">Ohne Liane-Pflegebox</option>
        </select>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{fehler}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">VersNr</th>
              <th className="text-left px-4 py-2">Liane</th>
              <th className="text-left px-4 py-2">Registriert am</th>
              <th className="text-left px-4 py-2">Beratungskunde?</th>
            </tr>
          </thead>
          <tbody>
            {laden && daten.items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Lade…</td></tr>
            ) : daten.items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Keine Treffer.</td></tr>
            ) : (
              daten.items.map((n) => (
                <tr key={n.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" />{n.email}</td>
                  <td className="px-4 py-2 text-gray-700">{[n.vorname, n.nachname].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-4 py-2 text-gray-500 font-mono text-xs">{n.versicherungsnummer || '—'}</td>
                  <td className="px-4 py-2">{n.liane_box ? <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs font-bold">Liane</span> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2 text-gray-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" />{n.registriert_am ? new Date(n.registriert_am).toLocaleDateString('de-DE') : '—'}</td>
                  <td className="px-4 py-2">{n.ist_beratungskunde ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-bold">ja</span> : <span className="text-gray-300">—</span>}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>{daten.total.toLocaleString('de-DE')} Treffer · Seite {seite + 1} von {seitenAnzahl}</div>
        <div className="flex gap-2">
          <button
            onClick={() => setSeite((s) => Math.max(0, s - 1))}
            disabled={seite === 0}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed bg-white"
          >Zurück</button>
          <button
            onClick={() => setSeite((s) => (s + 1 < seitenAnzahl ? s + 1 : s))}
            disabled={seite + 1 >= seitenAnzahl}
            className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed bg-white"
          >Weiter</button>
        </div>
      </div>
    </div>
  );
}


// ==========================================================================
// Zahlende Kunden — Stripe-Subscriptions im Pflegecockpit
// ==========================================================================
function ZahlendeKunden() {
  const [liste, setListe] = useState([]);
  const [stats, setStats] = useState(null);
  const [nurAktiv, setNurAktiv] = useState(true);
  const [laeuft, setLaeuft] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [syncLaeuft, setSyncLaeuft] = useState(false);
  const [syncErgebnis, setSyncErgebnis] = useState(null);

  const lade = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const [k, s] = await Promise.all([
        api.get(`/api/stripe/zahlende-kunden${nurAktiv ? '?nur_aktiv=true' : ''}`),
        api.get('/api/stripe/stats'),
      ]);
      setListe(Array.isArray(k) ? k : []);
      setStats(s || null);
    } catch (e) {
      setFehler(e?.detail || 'Daten konnten nicht geladen werden.');
    } finally {
      setLaeuft(false);
    }
  };

  useEffect(() => { lade(); /* eslint-disable-next-line */ }, [nurAktiv]);

  const syncJetzt = async () => {
    setSyncLaeuft(true);
    setSyncErgebnis(null);
    try {
      const r = await api.post('/api/stripe/sync-jetzt', {});
      setSyncErgebnis(`Sync OK — gesehen ${r.gesehen}, neu ${r.neu}, aktualisiert ${r.aktualisiert}, Fehler ${r.fehler}`);
      await lade();
    } catch (e) {
      setSyncErgebnis(`FEHLER: ${e?.detail || 'Sync fehlgeschlagen'}`);
    } finally {
      setSyncLaeuft(false);
    }
  };

  const formatPreis = (cents, currency, interval) => {
    if (cents == null) return '—';
    const eur = (cents / 100).toFixed(2);
    const sym = currency === 'eur' ? '€' : currency?.toUpperCase() || '';
    const intl = interval === 'year' ? '/Jahr' : interval === 'month' ? '/Monat' : '';
    return `${eur} ${sym}${intl}`;
  };

  const formatDatum = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const statusBadge = (status, cancelAtEnd) => {
    const farben = {
      active:     { bg: '#dcfce7', fg: '#15803d', label: 'aktiv' },
      trialing:   { bg: '#dbeafe', fg: '#1e40af', label: 'Probe' },
      past_due:   { bg: '#fef3c7', fg: '#92400e', label: 'Zahlung offen' },
      canceled:   { bg: '#f3f4f6', fg: '#6b7280', label: 'gekündigt' },
      incomplete: { bg: '#fee2e2', fg: '#991b1b', label: 'unvollständig' },
      unpaid:     { bg: '#fee2e2', fg: '#991b1b', label: 'unbezahlt' },
    };
    const f = farben[status] || { bg: '#f3f4f6', fg: '#6b7280', label: status || '?' };
    return (
      <span style={{ background: f.bg, color: f.fg, padding: '0.15rem 0.55rem',
                     borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>
        {f.label}{cancelAtEnd && status === 'active' ? ' (läuft aus)' : ''}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Zahlende Kunden</h2>
          <p className="text-sm text-gray-500">
            Stripe-Subscriptions, gespiegelt in die lokale DB. Sync stündlich,
            manuell jederzeit auslösbar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={nurAktiv} onChange={e => setNurAktiv(e.target.checked)} />
            nur aktive Abos
          </label>
          <button onClick={syncJetzt} disabled={syncLaeuft}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white text-sm font-medium">
            {syncLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Jetzt syncen
          </button>
        </div>
      </div>

      {syncErgebnis && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${syncErgebnis.startsWith('FEHLER') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {syncErgebnis}
        </div>
      )}

      {/* Stats-Kacheln */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatKachel icon={CreditCard} label="Aktive Abos" wert={stats.aktivGesamt} farbe="#15803d" />
          <StatKachel icon={TrendingUp} label="MRR" wert={`${stats.mrrEuro?.toFixed?.(2) || '0,00'} €`} farbe="#1d4ed8" />
          <StatKachel icon={CheckCircle2} label="Pflegecockpit-Match" wert={`${stats.matchedPflegecockpitNutzer}/${stats.aktivGesamt}`} farbe="#85366C" />
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">Verteilung</div>
            <div className="text-sm space-y-0.5">
              {Object.entries(stats.verteilungPlan || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-700 truncate mr-2">{k}</span>
                  <span className="font-bold">{v}</span>
                </div>
              ))}
              {Object.keys(stats.verteilungPlan || {}).length === 0 && (
                <span className="text-gray-400">—</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {laeuft && <div className="text-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
      {fehler && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>{fehler}</div>
        </div>
      )}
      {!laeuft && !fehler && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                <th className="p-3">Kunde</th>
                <th className="p-3">Abo</th>
                <th className="p-3">Preis</th>
                <th className="p-3">Status</th>
                <th className="p-3">Seit</th>
                <th className="p-3">Pflegecockpit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {liste.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-gray-400">
                  Keine zahlenden Kunden gefunden. Vielleicht erst syncen?
                </td></tr>
              )}
              {liste.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium text-gray-800">{s.name || '—'}</div>
                    <div className="text-xs text-gray-500">{s.email || '—'}</div>
                  </td>
                  <td className="p-3 text-gray-700">{s.planName || '—'}</td>
                  <td className="p-3 text-gray-700">
                    {formatPreis(s.planAmountCents, s.planCurrency, s.planInterval)}
                  </td>
                  <td className="p-3">{statusBadge(s.subscriptionStatus, s.cancelAtPeriodEnd)}</td>
                  <td className="p-3 text-gray-600 text-xs">
                    {formatDatum(s.subscriptionStartedAt)}
                    {s.subscriptionCurrentPeriodEnd && (
                      <div className="text-[10px] text-gray-400">
                        bis {formatDatum(s.subscriptionCurrentPeriodEnd)}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {s.pflegecockpitNutzerId
                      ? <span className="text-green-600 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> verknüpft
                        </span>
                      : <span className="text-gray-400 inline-flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> kein Match
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatKachel({ icon: Icon, label, wert, farbe }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold" style={{ color: farbe }}>{wert}</div>
    </div>
  );
}


// ==========================================================================
// Anfragen-Helpdesk: Liste links + Detail rechts
// ==========================================================================
function AnfragenAnsicht({ initialId }) {
  const [liste, setListe] = useState([]);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [bereichFilter, setBereichFilter] = useState('alle');
  const [suche, setSuche] = useState('');
  const [selectedId, setSelectedId] = useState(initialId || null);
  const [detail, setDetail] = useState(null);
  const [listeLoading, setListeLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // === Liste laden ===
  useEffect(() => {
    let abgebrochen = false;
    setListeLoading(true);
    setFehler(null);
    const params = new URLSearchParams();
    if (statusFilter !== 'alle') params.set('status', statusFilter);
    if (bereichFilter !== 'alle') params.set('bereich', bereichFilter);
    api.get(`/api/feedback/anfragen${params.toString() ? '?' + params : ''}`)
      .then(daten => {
        if (abgebrochen) return;
        setListe(daten || []);
        // Auto-Auswahl: erste Anfrage, wenn keine selektiert oder die selektierte
        // nicht mehr in der Liste ist.
        if ((daten || []).length > 0) {
          const noch_da = daten.find(a => a.id === selectedId);
          if (!selectedId || !noch_da) {
            setSelectedId(daten[0].id);
          }
        } else {
          setSelectedId(null);
        }
      })
      .catch(e => { if (!abgebrochen) setFehler(e?.detail || 'Anfragen konnten nicht geladen werden.'); })
      .finally(() => { if (!abgebrochen) setListeLoading(false); });
    return () => { abgebrochen = true; };
    // eslint-disable-next-line
  }, [statusFilter, bereichFilter, refreshKey]);

  // === Detail laden ===
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let abgebrochen = false;
    setDetailLoading(true);
    api.get(`/api/feedback/anfragen/${selectedId}`)
      .then(d => {
        if (abgebrochen) return;
        setDetail(d);
        // Auto-mark-read: wenn ungelesene eingehende Nachrichten dabei sind,
        // serverseitig als gelesen markieren und die Liste neu ziehen, damit
        // der rote Badge auf der Karte verschwindet.
        if ((d?.ungelesen_count || 0) > 0) {
          api.post(`/api/feedback/anfragen/${d.id}/gelesen`)
            .then(() => { if (!abgebrochen) setRefreshKey(k => k + 1); })
            .catch(() => { /* still funktional, Badge bleibt halt bis zum naechsten Reload */ });
        }
      })
      .catch(e => { if (!abgebrochen) alert(e?.detail || 'Detail konnte nicht geladen werden.'); })
      .finally(() => { if (!abgebrochen) setDetailLoading(false); });
    return () => { abgebrochen = true; };
  }, [selectedId, refreshKey]);

  const reload = () => setRefreshKey(k => k + 1);

  const handleDelete = async (anfrageId, kurzbeschreibung) => {
    if (!window.confirm(`Anfrage "${kurzbeschreibung}" wirklich löschen?\n\nDer komplette Konversations-Verlauf geht dabei verloren.`)) return;
    try {
      await api.del(`/api/feedback/anfragen/${anfrageId}`);
      // Wenn die geloeschte gerade selektiert war, Auswahl freigeben.
      if (selectedId === anfrageId) setSelectedId(null);
      reload();
    } catch (e) {
      alert(e?.detail || 'Loeschen fehlgeschlagen.');
    }
  };

  // === Filter (clientseitig) zusaetzlich nach Suche ===
  const gefilterteListe = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return liste;
    return liste.filter(a => {
      const t = `${a.name || ''} ${a.email || ''} ${a.telefon || ''} ${a.text_initial || ''}`.toLowerCase();
      return t.includes(q);
    });
  }, [liste, suche]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: 600 }}>
      {/* === Linke Spalte: Filter + Liste === */}
      <div className="lg:col-span-5 flex flex-col gap-3">
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg text-sm bg-white px-3 py-2"
          >
            <option value="alle">Alle Status</option>
            <option value="offen">Offen</option>
            <option value="in_bearbeitung">In Bearbeitung</option>
            <option value="erledigt">Erledigt</option>
          </select>
          <select
            value={bereichFilter}
            onChange={(e) => setBereichFilter(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg text-sm bg-white px-3 py-2"
          >
            <option value="alle">Alle Bereiche</option>
            {Object.entries(BEREICHE).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            onClick={reload}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            title="Neu laden"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Suche in Name, E-Mail, Text…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          />
        </div>

        {fehler && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {fehler}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-y-auto" style={{ maxHeight: 700 }}>
          {listeLoading && liste.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
            </div>
          ) : gefilterteListe.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">
              {liste.length === 0 ? 'Noch keine Anfragen vorhanden.' : 'Keine Treffer mit diesem Filter.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {gefilterteListe.map(a => (
                <ListeEintrag
                  key={a.id}
                  anfrage={a}
                  aktiv={a.id === selectedId}
                  onClick={() => setSelectedId(a.id)}
                  onDelete={() => handleDelete(a.id, a.name || a.email || a.telefon || 'ohne Kontakt')}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* === Rechte Spalte: Detail === */}
      <div className="lg:col-span-7">
        {!selectedId ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400 text-sm">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            Wähle links eine Anfrage aus.
          </div>
        ) : detailLoading || !detail ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade Anfrage…
          </div>
        ) : (
          <AnfrageDetail anfrage={detail} onChange={reload} />
        )}
      </div>
    </div>
  );
}


function ListeEintrag({ anfrage, aktiv, onClick, onDelete }) {
  const datum = anfrage.created_at ? new Date(anfrage.created_at).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '';
  const vorschau = (anfrage.text_initial || '').slice(0, 100);
  const statusFarbe = {
    offen: 'bg-amber-100 text-amber-800',
    in_bearbeitung: 'bg-blue-100 text-blue-800',
    erledigt: 'bg-emerald-100 text-emerald-800',
  }[anfrage.status] || 'bg-gray-100 text-gray-800';

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete?.();
  };

  const ungelesen = anfrage.ungelesen_count || 0;

  return (
    <li className="relative group">
      <button
        onClick={onClick}
        className={`w-full text-left px-4 py-3 pr-10 hover:bg-gray-50 transition-colors ${aktiv ? 'bg-fuchsia-50 border-l-4' : 'border-l-4 border-transparent'}`}
        style={aktiv ? { borderLeftColor: CI.brombeer } : {}}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
            {anfrage.bereich_label}
          </span>
          <div className="flex items-center gap-1.5">
            {ungelesen > 0 && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse"
                title={`${ungelesen} neue Antwort${ungelesen === 1 ? '' : 'en'} vom Kunden`}
              >
                {ungelesen} neu
              </span>
            )}
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusFarbe}`}>
              {STATUS_LABEL[anfrage.status]}
            </span>
          </div>
        </div>
        <div className={`text-sm truncate ${ungelesen > 0 ? 'font-extrabold text-gray-900' : 'font-bold text-gray-800'}`}>
          {anfrage.name || anfrage.email || anfrage.telefon || 'Anonym'}
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">{vorschau}</div>
        <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1.5">
          <span>{datum}</span>
          {anfrage.anzahl_nachrichten > 1 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> {anfrage.anzahl_nachrichten}
            </span>
          )}
        </div>
      </button>
      <button
        onClick={handleDeleteClick}
        className="absolute top-2 right-2 p-1.5 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        title="Anfrage löschen"
        aria-label="Anfrage löschen"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}


function AnfrageDetail({ anfrage, onChange }) {
  const [antwortText, setAntwortText] = useState('');
  // Jeder Eintrag: { file, previewUrl }. previewUrl nur bei Bildern (PNG/JPG),
  // sonst null. Object-URLs muessen wieder freigegeben werden (Memory-Leak).
  const [antwortDateien, setAntwortDateien] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [notizText, setNotizText] = useState('');
  const [notizKanal, setNotizKanal] = useState('telefon');
  const [internText, setInternText] = useState(anfrage.notiz_intern || '');
  const [busyAntwort, setBusyAntwort] = useState(false);
  const [busyNotiz, setBusyNotiz] = useState(false);
  const [busyIntern, setBusyIntern] = useState(false);
  const fileInputRef = useRef(null);

  // Alle Bild-Vorschau-URLs freigeben (zentral, damit nichts vergessen wird).
  const gibVorschauenFrei = (eintraege) =>
    eintraege.forEach((e) => e.previewUrl && URL.revokeObjectURL(e.previewUrl));

  const dateienHinzufuegen = (fileList) => {
    const neue = Array.from(fileList || []).map((file) => ({
      file,
      previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
    if (neue.length) setAntwortDateien((alt) => [...alt, ...neue]);
  };

  // Bei Anfrage-Wechsel: lokale States resetten (Form leeren).
  useEffect(() => {
    setAntwortText('');
    setAntwortDateien((alt) => { gibVorschauenFrei(alt); return []; });
    setDragOver(false);
    setNotizText('');
    setNotizKanal('telefon');
    setInternText(anfrage.notiz_intern || '');
  }, [anfrage.id]);

  // Beim Verlassen der Detailansicht uebrige Object-URLs aufraeumen.
  useEffect(() => () => {
    setAntwortDateien((alt) => { gibVorschauenFrei(alt); return alt; });
  }, []);

  const handleDateienWahl = (e) => {
    dateienHinzufuegen(e.target.files);
    e.target.value = '';  // gleicher Dateiname erneut waehlbar
  };
  const entferneDatei = (idx) =>
    setAntwortDateien((alt) => {
      const weg = alt[idx];
      if (weg?.previewUrl) URL.revokeObjectURL(weg.previewUrl);
      return alt.filter((_, i) => i !== idx);
    });

  const handleStatus = async (neuerStatus) => {
    try {
      await api.put(`/api/feedback/anfragen/${anfrage.id}`, { status: neuerStatus });
      onChange();
    } catch (e) {
      alert(e?.detail || 'Status-Update fehlgeschlagen.');
    }
  };

  const handleAntwort = async (e) => {
    e?.preventDefault();
    if (!antwortText.trim()) return;
    if (!anfrage.email) {
      alert('Diese Anfrage hat keine E-Mail-Adresse — bitte als Telefon-Notiz dokumentieren.');
      return;
    }
    setBusyAntwort(true);
    try {
      const fd = new FormData();
      fd.append('text', antwortText.trim());
      antwortDateien.forEach((d) => fd.append('anhaenge', d.file));
      await api.postForm(`/api/feedback/anfragen/${anfrage.id}/antwort`, fd);
      setAntwortText('');
      setAntwortDateien((alt) => { gibVorschauenFrei(alt); return []; });
      onChange();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Antwort konnte nicht gesendet werden.';
      alert(msg);
    } finally {
      setBusyAntwort(false);
    }
  };

  const handleNotiz = async (e) => {
    e?.preventDefault();
    if (!notizText.trim()) return;
    setBusyNotiz(true);
    try {
      await api.post(`/api/feedback/anfragen/${anfrage.id}/notiz`, {
        text: notizText.trim(),
        kanal: notizKanal,
      });
      setNotizText('');
      onChange();
    } catch (e) {
      alert(e?.detail || 'Notiz konnte nicht gespeichert werden.');
    } finally {
      setBusyNotiz(false);
    }
  };

  const handleInternSpeichern = async () => {
    setBusyIntern(true);
    try {
      await api.put(`/api/feedback/anfragen/${anfrage.id}`, { notiz_intern: internText });
      onChange();
    } catch (e) {
      alert(e?.detail || 'Notiz konnte nicht gespeichert werden.');
    } finally {
      setBusyIntern(false);
    }
  };

  const datum = anfrage.created_at ? new Date(anfrage.created_at).toLocaleString('de-DE') : '';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
              <span>{anfrage.bereich_label}</span>
              <span className="text-gray-300">·</span>
              <span>{datum}</span>
            </div>
            <h3 className="text-lg font-bold text-gray-800 truncate">
              {anfrage.name || 'Anonym'}
            </h3>
            <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-1">
              {anfrage.email && (
                <a href={`mailto:${anfrage.email}`} className="flex items-center gap-1 hover:underline" style={{ color: CI.brombeer }}>
                  <Mail className="w-3 h-3" /> {anfrage.email}
                </a>
              )}
              {anfrage.telefon && (
                <a href={`tel:${anfrage.telefon}`} className="flex items-center gap-1 hover:underline" style={{ color: CI.brombeer }}>
                  <Phone className="w-3 h-3" /> {anfrage.telefon}
                </a>
              )}
            </div>
          </div>
          <select
            value={anfrage.status}
            onChange={(e) => handleStatus(e.target.value)}
            className="border border-gray-300 rounded-lg text-sm bg-white px-3 py-2"
          >
            <option value="offen">Offen</option>
            <option value="in_bearbeitung">In Bearbeitung</option>
            <option value="erledigt">Erledigt</option>
          </select>
        </div>

        {anfrage.pflegecockpit_nutzer && (
          <div className="mt-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Bekannter Pflegecockpit-Nutzer
            {anfrage.pflegecockpit_nutzer.registriert_am && (
              <> — registriert seit {new Date(anfrage.pflegecockpit_nutzer.registriert_am).toLocaleDateString('de-DE')}</>
            )}
            {anfrage.pflegecockpit_nutzer.liane_box && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-bold">Liane-Pflegebox</span>
            )}
          </div>
        )}
      </div>

      {/* Konversations-Verlauf */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" /> Verlauf ({(anfrage.nachrichten || []).length})
        </h4>
        <div className="space-y-3">
          {(anfrage.nachrichten || []).map(n => (
            <NachrichtBlock key={n.id} nachricht={n} />
          ))}
        </div>
      </div>

      {/* Antwort-Mail */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-3">
          <Send className="w-3.5 h-3.5" /> Antwort per E-Mail
          {!anfrage.email && <span className="text-amber-600 font-normal normal-case">(keine E-Mail-Adresse — nutze stattdessen Telefon-Notiz unten)</span>}
        </h4>
        <form onSubmit={handleAntwort}>
          <textarea
            value={antwortText}
            onChange={(e) => setAntwortText(e.target.value)}
            placeholder={anfrage.email ? `Antwort an ${anfrage.email}…` : 'Keine E-Mail vorhanden'}
            disabled={!anfrage.email || busyAntwort}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300 disabled:bg-gray-50 disabled:text-gray-400"
          />

          {/* Drop-Zone fuer Anhaenge (Drag-and-Drop + Klick) */}
          <div
            onDragOver={(e) => { if (anfrage.email && !busyAntwort) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault(); setDragOver(false);
              if (anfrage.email && !busyAntwort) dateienHinzufuegen(e.dataTransfer.files);
            }}
            onClick={() => { if (anfrage.email && !busyAntwort) fileInputRef.current?.click(); }}
            className={`mt-2 border-2 border-dashed rounded-lg p-3 text-center transition-colors ${(!anfrage.email || busyAntwort) ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' : 'cursor-pointer ' + (dragOver ? 'border-fuchsia-400 bg-fuchsia-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100')}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleDateienWahl}
              disabled={!anfrage.email || busyAntwort}
              className="hidden"
            />
            <div className="text-sm text-gray-500">
              <UploadCloud className="w-5 h-5 mx-auto mb-1 text-gray-400" />
              Datei hierher ziehen oder{' '}
              <span className="font-bold text-fuchsia-700">klicken zum Auswählen</span>
              <div className="text-xs text-gray-400 mt-0.5">
                Bilder, PDF, Excel, Word u.a. — max. 20 MB insgesamt
              </div>
            </div>
          </div>

          {/* Vorschau-Karten der gewaehlten Anhaenge */}
          {antwortDateien.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {antwortDateien.map((d, i) => (
                <div
                  key={i}
                  className="relative group border border-gray-200 rounded-lg bg-white p-1.5 w-24"
                  title={d.file.name}
                >
                  <button
                    type="button"
                    onClick={() => entferneDatei(i)}
                    disabled={busyAntwort}
                    className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-0.5 text-gray-500 hover:text-red-500 shadow-sm disabled:opacity-50"
                    title="Anhang entfernen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {d.previewUrl ? (
                    <img
                      src={d.previewUrl}
                      alt={d.file.name}
                      className="w-full h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-full h-16 flex items-center justify-center bg-gray-50 rounded">
                      <FileText className="w-7 h-7 text-gray-400" />
                    </div>
                  )}
                  <div className="mt-1 text-[10px] text-gray-600 truncate">{d.file.name}</div>
                  <div className="text-[10px] text-gray-400">{(d.file.size / 1024).toFixed(0)} KB</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={!antwortText.trim() || !anfrage.email || busyAntwort}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold disabled:opacity-50"
              style={{ backgroundColor: CI.brombeer }}
            >
              {busyAntwort ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Antwort senden
            </button>
          </div>
        </form>
      </div>

      {/* Telefon-Notiz */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-3">
          <Phone className="w-3.5 h-3.5" /> Telefon-/Inhaltliche Notiz dokumentieren
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Wenn du den Kunden telefonisch erreichst — Kurznotiz hier eintragen, sie wird als Konversations-Eintrag gespeichert.
        </p>
        <form onSubmit={handleNotiz}>
          <textarea
            value={notizText}
            onChange={(e) => setNotizText(e.target.value)}
            placeholder="Kurze Notiz zum Telefongespräch oder zur Klärung…"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-300"
          />
          <div className="flex items-center justify-between mt-2 gap-2">
            <select
              value={notizKanal}
              onChange={(e) => setNotizKanal(e.target.value)}
              className="border border-gray-300 rounded-lg text-sm bg-white px-3 py-2"
            >
              <option value="telefon">Telefon</option>
              <option value="email">E-Mail (manuell)</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="sonstige">Sonstige</option>
            </select>
            <button
              type="submit"
              disabled={!notizText.trim() || busyNotiz}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-white font-bold disabled:opacity-50 hover:bg-gray-800"
            >
              {busyNotiz ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              Notiz hinzufügen
            </button>
          </div>
        </form>
      </div>

      {/* Interne Notiz (Scratchpad) */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-3">
          Interne Notiz (Scratchpad)
        </h4>
        <p className="text-xs text-amber-800 mb-3">
          Nur intern sichtbar, geht nicht an den Nutzer. Geeignet für Erinnerungen oder Zwischenstände.
        </p>
        <textarea
          value={internText}
          onChange={(e) => setInternText(e.target.value)}
          rows={3}
          placeholder='z.B. "muss bei Krankenkasse XY zurueckrufen"...'
          className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-300"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={handleInternSpeichern}
            disabled={busyIntern || internText === (anfrage.notiz_intern || '')}
            className="px-4 py-2 rounded-lg text-amber-900 font-bold border border-amber-300 bg-amber-100 hover:bg-amber-200 disabled:opacity-50 flex items-center gap-2"
          >
            {busyIntern && <Loader2 className="w-4 h-4 animate-spin" />}
            Notiz speichern
          </button>
        </div>
      </div>
    </div>
  );
}


function NachrichtBlock({ nachricht }) {
  const istAusgehend = nachricht.richtung === 'ausgehend';
  const datum = nachricht.created_at ? new Date(nachricht.created_at).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '';
  const kanalLabel = {
    widget: 'Widget',
    email: 'E-Mail',
    telefon: 'Telefon',
    whatsapp: 'WhatsApp',
    sonstige: 'Sonstige',
  }[nachricht.kanal] || nachricht.kanal;

  const wrapper = istAusgehend
    ? 'bg-emerald-50 border-emerald-200 ml-8'
    : 'bg-gray-50 border-gray-200 mr-8';
  const richtungIcon = istAusgehend ? <Send className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />;

  return (
    <div className={`border rounded-lg p-3 ${wrapper}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">
          {richtungIcon}
          {istAusgehend ? 'Wir' : 'Kunde'} · {kanalLabel}
        </div>
        <div className="text-[11px] text-gray-400 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {datum}
        </div>
      </div>
      <div className="text-sm text-gray-800 whitespace-pre-wrap">{nachricht.text}</div>
    </div>
  );
}
